<template>
  <div
    class="flex h-full max-h-full min-h-0 flex-1 flex-col overflow-hidden rounded-md"
  >
    <template v-if="commissionNote">
      <PdfViewer
        ref="pdf-viewer"
        :loader="loadPdf"
        :title="downloadTitle"
        :back-route="{
          name: 'commission_note_show_route',
          params: { id: commissionNote.id }
        }"
        :mail-subject="mailDefaults?.subject"
        reverse-panel-position
        @sended="traceMailSent"
      >
        <template #option-pane>
          <CommissionNotePrintMenu
            :options="options"
            :headers="headers"
            :footers="footers"
            :can-send="canSend"
            @refresh="refreshDocument"
            @send="sendByMail"
            @print="$refs['pdf-viewer'].print()"
            @save="save"
          />
        </template>
      </PdfViewer>
    </template>
    <CommissionNoteLoadError
      v-else-if="loadFailed"
      class="bg-gray-700 text-white"
      @retry="fetchCommissionNote"
    />
    <div v-else class="flex flex-1 items-center justify-center bg-gray-700">
      <LoadingComponent />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import PdfViewer from '@c/Media/PdfViewer.vue'
import DocTypes from '@/assets/enums/invoicing/DocTypes'
import LoadingComponent from '@c/TheLoadingComponent.vue'
import CommissionNoteLoadError from '@/views/invoicing/show/commissionNote/components/CommissionNoteLoadError.vue'
import CommissionNotePrintMenu from '@/views/invoicing/show/commissionNote/components/CommissionNotePrintMenu.vue'
import {
  printOptionsFor,
  printQuery
} from '@/views/invoicing/show/commissionNote/components/commissionNotePrint'

/**
 * Print page of a commission note, like the one of the invoices: the PDF on
 * the left, the menu of the print options on the right (the PDF is rebuilt
 * by the API on refresh), and the way to send it by e-mail.
 */
export default {
  name: 'TheCommissionNotePrintPage',
  components: {
    CommissionNoteLoadError,
    CommissionNotePrintMenu,
    LoadingComponent,
    PdfViewer
  },
  data() {
    return {
      commissionNote: null,
      loadFailed: false,
      options: {},
      headers: [],
      footers: null,
      mailDefaults: null
    }
  },
  computed: {
    ...mapGetters({ funeralHomeSettings: 'metadata/getFuneralHomeSettings' }),
    canSend() {
      return (
        this.$can('manage_commission_notes') ||
        this.$can('manage_commission_settings')
      )
    },
    downloadTitle() {
      return (this.commissionNote.reference ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[/\\:*?"<>|]/g, '-')
    }
  },
  watch: {
    '$route.params.id': {
      handler(id) {
        // leaving the page empties the param before the component is unmounted
        if (!id) return
        this.fetchCommissionNote()
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions({
      processFetchAction: 'commissionNote/fetchCommissionNote',
      processPdfAction: 'commissionNote/pdfCommissionNoteInline',
      processHeadersAction: 'commissionNote/fetchCommissionNoteHeaders',
      processMailDefaultsAction:
        'commissionNote/fetchCommissionNoteMailDefaults',
      processMailSentAction: 'commissionNote/commissionNoteMailSent'
    }),
    fetchCommissionNote() {
      this.commissionNote = null
      // what the note we leave had brought must not stay while the next one loads
      this.headers = []
      this.footers = null
      this.mailDefaults = null
      this.loadFailed = false
      const targetId = this.$route.params.id

      this.processFetchAction({ id: targetId })
        .then((response) => {
          if (this.$route.params.id !== targetId) return
          const note = response.commission_note

          this.options = printOptionsFor(
            this.funeralHomeSettings?.[note.funeral_home?.id]
              ?.invoicing_printing_settings?.[DocTypes.COMMISSION_NOTE],
            note.language
          )
          this.commissionNote = note
          this.processHeadersAction({ id: note.id })
            .then((headers) => {
              if (this.$route.params.id !== targetId) return
              this.headers = headers.headers ?? []
              this.footers = headers.footers ?? null
            })
            .catch(() => {})
          this.processMailDefaultsAction({ id: note.id })
            .then((defaults) => {
              if (this.$route.params.id !== targetId) return
              this.mailDefaults = defaults
            })
            .catch(() => {})
        })
        .catch((error) => {
          if (this.$route.params.id !== targetId) return
          // a missing or forbidden note leaves the page; any other failure shows a message and a way to try again
          if ([403, 404].includes(error?.response?.status)) {
            this.$router.push({ name: 'commission_note_index_route' })
            return
          }

          this.loadFailed = true
        })
    },
    // the mail left from the mail window: the note keeps a trace of it in its history
    traceMailSent({ receivers } = {}) {
      this.processMailSentAction({
        id: this.commissionNote.id,
        receivers: receivers ?? []
      }).catch(() => {})
    },
    // the API answers the PDF itself
    loadPdf() {
      return this.processPdfAction({
        id: this.commissionNote.id,
        options: printQuery(this.options)
      })
    },
    refreshDocument() {
      this.$refs['pdf-viewer'].reload()
    },
    save() {
      this.$refs['pdf-viewer'].download()
    },
    // the mail modal of the reader attaches the PDF as shown, the advisor is the receiver
    sendByMail() {
      this.$refs['pdf-viewer'].openMailWithOptions({
        subject: this.mailDefaults?.subject,
        body: this.mailDefaults?.message,
        recipient: this.mailDefaults?.receivers?.[0]
      })
    }
  }
}
</script>
